import React, { useEffect, useMemo, useState } from "react";
import MiniTableProEditor from "./MiniTableProEditor";
import MiniTableProPrint from "./MiniTableProPrint";
import { MINI_TABLE_MAX_PROPERTIES } from "./constants";
import { IsValidSessionSwitch } from "common/storage";
import { getPropertyId } from "./utils";

export { MINI_TABLE_MAX_PROPERTIES } from "./constants";

const EDIT_ENABLED_LISTGEN_IDS = new Set([
  "109954",
  "109973",
  "110067",
  "109938",
]);

function resolveMiniTableMaxRows(dto) {
  const n = dto?.miniTableMaxRows;
  return n > 0 ? n : MINI_TABLE_MAX_PROPERTIES;
}

/**
 * Chooses Editor (with edit UI) or Print (display only) based on printVersion.
 * Accepts miniTableDetailsDTO from parent; list is sliced to `miniTableMaxRows` from the DTO (or default 12).
 * @param {boolean} [fromThirdPage] - When true, applies `top-405-mini-table` (top: 405px) on `.print-ye2-mini-market-sales-table`
 * @param {boolean} [moveQRAndTableDown] - When true, applies `top-446-mini-table` (takes priority over fromThirdPage)
 */
function MiniTablePro({
  printVersion,
  miniTableDetailsDTO,
  moveQRAndTableDown,
  propertyData,
  ...props
}) {
  // Keep this gate at top-level: Approved mailings must render print-only
  // so all edit controls stay disabled without changing downstream files.
  // Mirrors the printVersion && !fromEditor convention used elsewhere
  // (e.g. PMAEditor.jsx) so this only forces print-only in actual print
  // output, not in the editor's view of an approved area.
  const effectivePrintVersion =
    printVersion ||
    (props?.currentArea?.mailingStatus === "Approved" && !props?.fromEditor);

  const maxRows = resolveMiniTableMaxRows(miniTableDetailsDTO);
  const rawList = miniTableDetailsDTO?.miniTable;
  // Memoized on the DTO's actual array reference (not re-sliced every render):
  // MiniTableProEditor resets its local edit state whenever this prop's
  // reference changes, so an unrelated re-render here (e.g. a header/footer
  // text save updating live* state below) must NOT produce a new array or
  // it will wipe out in-progress table edits that haven't round-tripped
  // back into miniTableDetailsDTO yet.
  const miniTableProperties = useMemo(
    () => (Array.isArray(rawList) ? rawList.slice(0, maxRows) : []),
    [rawList, maxRows]
  );

  // MailPro's getMailerProDetails data (propertyData), keyed by listingID so each
  // Mini Table row can look up its matching record for photos/extended details.
  // Coverage is partial by design — not every comp is also in the area's mailing
  // list — rows with no match simply render without a photo/extended dialog.
  const mailProPropertyByListingId = useMemo(() => {
    const map = new Map();
    if (Array.isArray(propertyData)) {
      for (const record of propertyData) {
        const listingId = getPropertyId(record?.propertyDetails);
        if (listingId) map.set(listingId, record);
      }
    }
    return map;
  }, [propertyData]);

  const miniTableDetailsID = miniTableDetailsDTO?.miniTableDetailsID;
  const deletedMiniTableProperties = miniTableDetailsDTO?.deletedMiniTableProperties ?? [];
  
  const miniTableTitle = miniTableDetailsDTO?.miniTableTitle ?? "";
  const miniTableSubTitle = miniTableDetailsDTO?.miniTableSubTitle ?? "";
  const miniTableDisclaimer1 = miniTableDetailsDTO?.miniTableDisclaimer1 ?? null;
  const miniTableDisclaimer2 = miniTableDetailsDTO?.miniTableDisclaimer2 ?? null;
  const miniTableDisclaimer3 = miniTableDetailsDTO?.miniTableDisclaimer3 ?? null;
  const miniTableDisclaimer4 = miniTableDetailsDTO?.miniTableDisclaimer4 ?? null;
  const offMarketDisclaimerBool = miniTableDetailsDTO?.offMarketDisclaimerBool ?? false;
  const coListingDisclaimerBool = miniTableDetailsDTO?.coListingDisclaimerBool ?? false;
  const miniTableDateRange = miniTableDetailsDTO?.miniTableDateRange ?? "";
  const miniTableAddedDate = miniTableDetailsDTO?.miniTableAddedDate ?? null;
  const miniTableUpdatedDate = miniTableDetailsDTO?.miniTableUpdatedDate ?? null;
  const miniTableDoNotUpdate = miniTableDetailsDTO?.miniTableDoNotUpdate ?? false;
  const miniTableStartDate = miniTableDetailsDTO?.miniTableStartDate ?? null;
  const [liveMiniTableTitle, setLiveMiniTableTitle] = useState(miniTableTitle);
  const [liveMiniTableSubTitle, setLiveMiniTableSubTitle] = useState(miniTableSubTitle);
  const [liveMiniTableDisclaimer2, setLiveMiniTableDisclaimer2] = useState(miniTableDisclaimer2);
  const [liveMiniTableDisclaimer3, setLiveMiniTableDisclaimer3] = useState(miniTableDisclaimer3);
  const [liveMiniTableDisclaimer4, setLiveMiniTableDisclaimer4] = useState(miniTableDisclaimer4);

  useEffect(() => {
    setLiveMiniTableTitle(miniTableTitle);
    setLiveMiniTableSubTitle(miniTableSubTitle);
  }, [miniTableDetailsID, miniTableTitle, miniTableSubTitle]);

  useEffect(() => {
    setLiveMiniTableDisclaimer2(miniTableDisclaimer2);
    setLiveMiniTableDisclaimer3(miniTableDisclaimer3);
    setLiveMiniTableDisclaimer4(miniTableDisclaimer4);
  }, [miniTableDetailsID, miniTableDisclaimer2, miniTableDisclaimer3, miniTableDisclaimer4]);
  // NOTE: Temporarily disabled.
  // Mini table is enabled for all agents now; parent controls print/edit mode via printVersion.
  // const listgenUserID = String(props?.agentData?.listgenUserID ?? "");
  // if (!printVersion && (IsValidSessionSwitch() || EDIT_ENABLED_LISTGEN_IDS.has(listgenUserID))) {
  //   printVersion = false;
  // } else {
  //   printVersion = true;
  // }

  const passThrough = {
    ...props,
    moveQRAndTableDown,
    miniTableProperties,
    maxRows,
    miniTableMaxRows: maxRows,
    ...(miniTableDetailsID != null && miniTableDetailsID !== "" && { miniTableDetailsID }),
    miniTableTitle: liveMiniTableTitle,
    miniTableSubTitle: liveMiniTableSubTitle,
    miniTableDisclaimer1,
    miniTableDisclaimer2: liveMiniTableDisclaimer2,
    miniTableDisclaimer3: liveMiniTableDisclaimer3,
    miniTableDisclaimer4: liveMiniTableDisclaimer4,
    offMarketDisclaimerBool,
    coListingDisclaimerBool,
    miniTableDateRange,
    miniTableAddedDate,
    miniTableUpdatedDate,
    miniTableDoNotUpdate,
    miniTableStartDate,
    mailProPropertyByListingId,
  };

  if (effectivePrintVersion) {
    return <MiniTableProPrint {...passThrough} printVersion={effectivePrintVersion} />;
  }
  return (
    <MiniTableProEditor
      deletedMiniTableProperties={deletedMiniTableProperties}
      onHeaderTextUpdated={({ miniTableTitle: nextTitle, miniTableSubTitle: nextSubTitle }) => {
        setLiveMiniTableTitle(nextTitle ?? "");
        setLiveMiniTableSubTitle(nextSubTitle ?? "");
      }}
      onFooterTextUpdated={({ miniTableDisclaimer2: nextDisclaimer2, miniTableDisclaimer3: nextDisclaimer3, miniTableDisclaimer4: nextDisclaimer4 }) => {
        setLiveMiniTableDisclaimer2(nextDisclaimer2 ?? "");
        setLiveMiniTableDisclaimer3(nextDisclaimer3 ?? "");
        setLiveMiniTableDisclaimer4(nextDisclaimer4 ?? "");
      }}
      {...passThrough}
      printVersion={effectivePrintVersion}
    />
  );
}

export default MiniTablePro;
